// De faselade (ontwerp "Fases op het bord"): de hele fase, ook het deel bij collega's, per persoon, met de subfases en
// waarop de fase wacht. Alleen lezen: Planner rekent de datums en de voortgang van een fase uit haar taken; wie iets wil
// wijzigen, doet dat op de taken. De schil (Layout) toont ze bij ?fase=; een taak openen zet ?taak=, en dat opent de
// taaklade van het scherm.
import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { catOf, labelColor } from "../lib/board";
import { faseChain, tasksByFase } from "../lib/fases";
import { useIndex, useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { deadline, plannerTaskUrl } from "../lib/planning";
import type { Fase, PlanningData, Task } from "../lib/types";
import { Av } from "./Avatar";

const NONE = "__none";

/** In de schil (Layout): de faselade bij ?fase=. Een taak of andere fase openen vervangt die in het adres. */
export function FaseDrawerHost() {
  const { data } = usePlanning();
  const [params, setParams] = useSearchParams();
  const id = params.get("fase");
  const fase = id && data ? data.fases.find((f) => f.id === id) : null;
  if (!fase || !data) return null;
  const go = (key: "taak" | "fase", v: string | null) => setParams((p) => { p.delete("taak"); p.delete("fase"); if (v) p.set(key, v); return p; });
  return <FaseDrawer key={fase.id} fase={fase} data={data} onClose={() => go("fase", null)} onOpenTask={(t) => go("taak", t)} onOpenFase={(f) => go("fase", f)} />;
}

export function FaseDrawer({ fase, data, onClose, onOpenTask, onOpenFase }: {
  fase: Fase; data: PlanningData; onClose: () => void; onOpenTask: (id: string) => void; onOpenFase: (id: string) => void;
}) {
  const { data: me } = useMe();
  const idx = useIndex(data);
  const [shown, setShown] = useState<string | null | undefined>(undefined);
  if (!me) return null;

  const above = faseChain(fase.parentId, idx.fases);
  const klant = idx.klanten.get(fase.projectId)?.name || "";
  const bucket = idx.buckets.get(fase.bucketId)?.name || "";
  const all = tasksByFase(data.tasks, idx.fases).get(fase.id) || [];
  const subs = data.fases.filter((f) => f.parentId === fase.id).sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  const openIn = (f: Fase) => all.filter((t) => faseChain(t.faseId, idx.fases).some((x) => x.id === f.id)).length;
  const d = deadline(fase.end, data.today);
  const pct = fase.total ? Math.round((fase.done / fase.total) * 100) : 0;
  const name = (k: string) => (k === NONE ? "Zonder eigenaar" : idx.people.get(k)?.name || "Onbekend");

  // per persoon; een taak met twee eigenaars telt bij beiden
  const per = new Map<string, Task[]>();
  for (const t of all) for (const k of t.owners.length ? t.owners.map((o) => o.personId) : [NONE]) per.set(k, [...(per.get(k) || []), t]);
  const rows = [...per].sort((a, b) => b[1].length - a[1].length || name(a[0]).localeCompare(name(b[0])));
  const max = Math.max(1, ...rows.map((r) => r[1].length));
  const mine = per.has(me.id) ? me.id : null;
  const openRow = shown === undefined ? mine ?? rows[0]?.[0] ?? null : shown;
  const stand = (t: Task) => { const c = catOf(t); return c === "actief" ? deadline(t.end, data.today) : { text: c === "geparkeerd" ? "geparkeerd" : "af te sluiten", tone: "" }; };
  const sub = (ts: Task[]) => {
    const c = { actief: 0, geparkeerd: 0, afsluiten: 0 };
    for (const t of ts) c[catOf(t)]++;
    return [c.actief ? c.actief + " actief" : "", c.geparkeerd ? c.geparkeerd + " geparkeerd" : "", c.afsluiten ? c.afsluiten + " af te sluiten" : ""].filter(Boolean).join(" · ");
  };
  // een taak in een subfase: de lagen tussen deze fase en de taak
  const below = (t: Task) => { const c = faseChain(t.faseId, idx.fases); const i = c.findIndex((f) => f.id === fase.id); return c.slice(i + 1).map((f) => f.title).join(" › "); };
  const preds = data.deps.filter((x) => x.succ === fase.id), succs = data.deps.filter((x) => x.pred === fase.id);
  const titleOf = (id: string) => idx.tasks.get(id)?.title || (idx.fases.get(id) ? "Fase · " + idx.fases.get(id)!.title : "Een afgewerkte taak");

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Fase sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="fdt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <p className="ex-eyebrow" style={{ margin: 0 }}>{klant}{bucket ? " · " + bucket : ""}</p>
              {above.length > 0 && (
                <div className="p-dw-pad">
                  <Icon name="fase" size={13} />
                  {above.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Deze fase openen" onClick={() => onOpenFase(f.id)}>{f.title}</button></Fragment>)}
                </div>
              )}
              <h2 id="fdt" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span className="p-fk-chip"><Icon name="fase" size={11} />Fase</span>{fase.title}</h2>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <Link className="ex-btn" to={"/taken?fase=" + fase.id} title="De fase uitwerken: omschrijving, checklist, labels, Clockify-project"><Icon name="checklist" size={15} />Volledig bewerken</Link>
              <a className="ex-btn" href={plannerTaskUrl(me, fase)} target="_blank" rel="noreferrer" title="Deze fase openen in Planner"><Icon name="ext" size={15} />Planner</a>
              <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={"ex-pill ex-pill--" + (all.length ? d.tone || "off" : "off")}><span className="ex-dot" />{d.long}</span>
            <span className="p-fk-prog" title={fase.done + " van " + fase.total + " taken af"} style={{ fontSize: 13 }}><i><b style={{ width: pct + "%" }} /></i>{fase.done} van {fase.total} af</span>
            <span className="t-mute" style={{ fontSize: 13 }}>volgens Planner {Math.round(fase.progress * 100)} % klaar</span>
            {fase.labels.map((l) => <span key={l.id} className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span>)}
          </div>
        </div>

        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs">
            <h3>Per persoon · {all.length === 1 ? "1 open taak" : all.length + " open taken"}</h3>
            {!rows.length && <p className="p-kd-note" style={{ margin: 0 }}>Geen open taken meer in deze fase.</p>}
            {rows.length > 0 && (
              <div className="p-fdw-who">
                {rows.map(([k, ts]) => {
                  const p = k === NONE ? null : idx.people.get(k), on = openRow === k;
                  return (
                    <Fragment key={k}>
                      <button type="button" className="p-fdw-r" aria-expanded={on} onClick={() => setShown(on ? null : k)}>
                        {p ? <Av id={p.id} init={p.initials} cls="p-av s" /> : <span className="p-av none s"><Icon name="userx" size={12} /></span>}
                        <span className="n">{name(k)}{k === me.id ? " (u)" : ""}<small>{sub(ts)}</small></span>
                        <span className="p-load"><i style={{ width: Math.round((ts.length / max) * 100) + "%" }} /></span>
                        <b>{ts.length}</b>
                        <Icon name={on ? "down" : "right"} size={14} />
                      </button>
                      {on && (
                        <div className="p-fdw-tasks">
                          {ts.slice().sort((a, b) => (catOf(a) === "actief" ? 0 : 1) - (catOf(b) === "actief" ? 0 : 1) || a.end.localeCompare(b.end)).map((t) => {
                            const s = stand(t), b2 = below(t);
                            return (
                              <button key={t.id} type="button" className="p-fdw-t" onClick={() => onOpenTask(t.id)}>
                                <span className="t">{t.title}{b2 && <small>{b2}</small>}</span>
                                <span className={s.tone ? "t-" + s.tone : "t-mute"}>{s.text}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {subs.length > 0 && (
            <div className="p-fs"><h3>Subfases · {subs.length}</h3>
              {subs.map((f) => {
                const n = openIn(f), sd = deadline(f.end, data.today);
                return (
                  <button key={f.id} type="button" className="p-fdw-sub" onClick={() => onOpenFase(f.id)}>
                    <span className="p-dep-ic"><Icon name="fase" size={14} /></span>
                    <span className="t"><b>{f.title}</b><small>{f.done} van {f.total} af · {n ? (n === 1 ? "1 open taak" : n + " open taken") + " · " + sd.text : "niets meer open"}</small></span>
                    <Icon name="right" size={14} />
                  </button>
                );
              })}
            </div>
          )}

          {(preds.length > 0 || succs.length > 0) && (
            <div className="p-fs"><h3>Wacht op en blokkeert</h3>
              {preds.map((x) => <div key={x.id} className="p-dep"><span className="p-dep-ic"><Icon name="arrow" size={14} /></span><span className="t"><b>{titleOf(x.pred)}</b><small>deze fase wacht hierop</small></span></div>)}
              {succs.map((x) => <div key={x.id} className="p-dep"><span className="p-dep-ic"><Icon name="arrow" size={14} /></span><span className="t"><b>{titleOf(x.succ)}</b><small>wacht op deze fase</small></span></div>)}
            </div>
          )}
        </div>

        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>Datums en voortgang rekent Planner uit de taken.</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Sluiten</button>
            <a className="ex-btn ex-btn--primary" href={plannerTaskUrl(me, fase)} target="_blank" rel="noreferrer"><Icon name="ext" size={15} />Openen in Planner</a>
          </span>
        </div>
      </aside>
    </>
  );
}
